/**
 * Barra de filtros do Painel de Assistências v2: período (atalhos ou datas),
 * data-base, tipos e N filtros dimensionais no padrão Excel. As opções de
 * cada dimensão vêm de /painel-assistencias/opcoes com contagem sob os
 * DEMAIS filtros (igual ao Excel) e só são buscadas quando a pessoa chega
 * perto do filtro — o painel não dispara 15 requests ao abrir.
 */
import React, { useMemo, useRef, useState } from "react";
import { FiSliders, FiX } from "react-icons/fi";

import FiltroExcel from "../../../components/FiltroExcel";
import { useAuth } from "../../../context/AuthContext";
import { listarOpcoesPainel } from "../../../services/solicitacoes";
import { DATAS_BASE, DIMENSOES, PERIODOS, filtrosAtivos, paraQuery } from "./recorte";

function FiltroDimensao({ dim, estado, onChange }) {
  const { request } = useAuth();
  const [opcoes, setOpcoes] = useState([]);
  const carregadoPara = useRef(null);
  const selecionadas = estado.filtros[dim.chave] ?? null;

  // Query SEM esta dimensão: a contagem de cada opção é sob os outros filtros.
  const consulta = useMemo(() => {
    const filtros = { ...estado.filtros };
    delete filtros[dim.chave];
    return paraQuery({ ...estado, filtros });
  }, [estado, dim.chave]);
  const chave = JSON.stringify(consulta);

  const carregar = () => {
    if (carregadoPara.current === chave) return;
    carregadoPara.current = chave;
    listarOpcoesPainel(request, consulta, dim.chave)
      .then((r) => setOpcoes((r?.opcoes || []).map((o) => ({ value: o.valor, label: o.label, count: o.total }))))
      .catch(() => { carregadoPara.current = null; });
  };

  // Valor já selecionado que saiu das opções (outro filtro o zerou) continua
  // visível para poder ser desmarcado.
  const opcoesVisiveis = useMemo(() => {
    if (!selecionadas) return opcoes;
    const presentes = new Set(opcoes.map((o) => o.value));
    const orfas = [...selecionadas].filter((v) => !presentes.has(v))
      .map((v) => ({ value: v, label: estado.rotulos?.[`${dim.chave}:${v}`] || v, count: 0 }));
    return [...opcoes, ...orfas];
  }, [opcoes, selecionadas, estado.rotulos, dim.chave]);

  return (
    <div onMouseEnter={carregar} onFocusCapture={carregar} onTouchStart={carregar}>
      <FiltroExcel rotulo={dim.rotulo} opcoes={opcoesVisiveis} selecionadas={selecionadas}
                   onChange={(sel) => onChange(dim.chave, sel)} />
    </div>
  );
}

export default function FiltrosBar({ estado, tiposNoEscopo, onEstado }) {
  const [mais, setMais] = useState(false);
  const personalizado = Boolean(estado.de || estado.ate);
  const ativos = filtrosAtivos(estado);
  const temAlgo = ativos.length > 0 || estado.tipos;

  const mudar = (patch) => onEstado({ ...estado, ...patch });
  const mudarFiltro = (chave, sel) => {
    const filtros = { ...estado.filtros };
    if (sel === null) delete filtros[chave]; else filtros[chave] = sel;
    mudar({ filtros });
  };
  const alternarTipo = (t) => {
    const base = estado.tipos ? new Set(estado.tipos) : new Set();
    if (base.has(t)) base.delete(t); else base.add(t);
    mudar({ tipos: base.size === 0 || base.size === tiposNoEscopo.length ? null : base });
  };

  const visiveis = DIMENSOES.filter((d) => d.principal || mais || estado.filtros[d.chave]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div className="tabchip-group">
          {PERIODOS.map((p) => (
            <div key={p.value} role="button" tabIndex={0}
                 className={"tabchip" + (!personalizado && estado.periodo === p.value ? " active" : "")}
                 onClick={() => mudar({ periodo: p.value, de: "", ate: "" })}
                 onKeyDown={(e) => e.key === "Enter" && mudar({ periodo: p.value, de: "", ate: "" })}>
              {p.label}
            </div>
          ))}
        </div>
        <label className="tiny muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          de
          <input type="date" className="input" value={estado.de} max={estado.ate || undefined}
                 onChange={(e) => mudar({ de: e.target.value })} style={{ height: 32 }} />
          até
          <input type="date" className="input" value={estado.ate} min={estado.de || undefined}
                 onChange={(e) => mudar({ ate: e.target.value })} style={{ height: 32 }} />
        </label>
        <label className="tiny muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
               title="Qual data coloca a solicitação dentro do período">
          contar por
          <select className="input" value={estado.data_base} style={{ height: 32 }}
                  onChange={(e) => mudar({ data_base: e.target.value })}>
            {DATAS_BASE.map((d) => <option key={d.value} value={d.value} title={d.hint}>{d.label}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div className="tabchip-group" title="Tipos de solicitação (nenhum marcado = todos)">
          {tiposNoEscopo.map((t) => (
            <div key={t} role="button" tabIndex={0}
                 className={"tabchip" + (estado.tipos?.has(t) ? " active" : "")}
                 onClick={() => alternarTipo(t)}
                 onKeyDown={(e) => e.key === "Enter" && alternarTipo(t)}>
              {t}
            </div>
          ))}
        </div>
        {visiveis.map((d) => (
          <FiltroDimensao key={d.chave} dim={d} estado={estado} onChange={mudarFiltro} />
        ))}
        <button type="button" className="btn btn--ghost" onClick={() => setMais((v) => !v)}>
          <FiSliders size={13} /> {mais ? "Menos filtros" : "Mais filtros"}
        </button>
        {temAlgo && (
          <button type="button" className="btn btn--ghost" onClick={() => mudar({ tipos: null, filtros: {}, rotulos: {} })}>
            <FiX size={13} /> Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}
